import React, { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from "react";
import { valueAtPath, valuesMatch } from "./aiAssistantProjection";
import "./aiAssistantFilledFields.css";

// The pause between two fields the lawyer can see, so the assistant's marks appear one after another.
export const FILLED_FIELD_STEP_MS = 350;

const FLASH_ANIMATION = /^ai-assistant-field-flash-/;

export const AiAssistantFilledFieldsContext = createContext({ marks: {}, registry: null });

// The form fields currently on screen. Only the visual reveal pauses between fields the lawyer can see.
export function createFilledFieldRegistry() {
  const counts = new Map();
  return {
    register(path) {
      counts.set(path, (counts.get(path) || 0) + 1);
      return () => {
        const count = (counts.get(path) || 1) - 1;
        if (count > 0) counts.set(path, count);
        else counts.delete(path);
      };
    },
    isShown: (path) => counts.has(path)
  };
}

// A mark lasts only while the field still holds the value the assistant wrote: a lawyer's edit or a form rule
// that resets the field takes it away.
export function currentFilledMarks(marks, form) {
  const current = Object.keys(marks || {}).reduce((current, path) => {
    if (valuesMatch(valueAtPath(form, path), marks[path].value)) current[path] = marks[path];
    return current;
  }, {});
  return marks && Object.keys(current).length === Object.keys(marks).length ? marks : current;
}

export function dropFilledMarks(marks, shouldDrop) {
  const paths = Object.keys(marks || {}).filter(shouldDrop);
  if (!paths.length) return null;
  const next = { ...marks };
  paths.forEach((path) => delete next[path]);
  return next;
}

// Rows of a list field (`...preCalcPartsData[2].percentBought`) at or after the given index.
export function isRowAtOrAfter(path, listPath, index) {
  const match = path.match(/^(.+)\[(\d+)\]\./);
  return Boolean(match && match[1] === listPath && Number(match[2]) >= index);
}

export function prefersReducedMotion() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

const FILLED_DESCRIPTION = "הוזן על ידי העוזר";

let lastDescriptionId = 0;

export function useAiAssistantFilledField(path) {
  const { marks, registry } = useContext(AiAssistantFilledFieldsContext);
  const mark = path ? marks[path] : undefined;
  const flashId = mark ? mark.flashId : null;
  // A value written before the field appeared on screen keeps its mark without a flash.
  const seenFlashId = useRef(flashId);
  const descriptionId = useRef(null);
  const [flashingId, setFlashingId] = useState(null);
  if (path && !descriptionId.current) descriptionId.current = `ai-assistant-filled-${++lastDescriptionId}`;

  // Registered before the container's state callback runs, so the container knows which fields are visible.
  useLayoutEffect(() => (path && registry ? registry.register(path) : undefined), [path, registry]);

  useEffect(() => {
    if (flashId === null || flashId === seenFlashId.current) return;
    seenFlashId.current = flashId;
    setFlashingId(flashId);
  }, [flashId]);

  const flashing = Boolean(mark) && flashingId !== null;
  return {
    filled: Boolean(mark),
    // Two animation names, so a new flash restarts even if the previous one is still playing.
    flashClassName: flashing ? `ai-assistant-field-flash-${flashingId % 2}` : "",
    descriptionId: descriptionId.current,
    onAnimationEnd: (event) => {
      if (FLASH_ANIMATION.test(event.animationName)) setFlashingId(null);
    }
  };
}

export function AiAssistantFilledBadge({ id, arriving }) {
  return (
    <span className={`ai-assistant-filled-badge${arriving ? " is-arriving" : ""}`}>
      <svg viewBox="0 0 16 16" aria-hidden="true" focusable="false">
        <path d="M6.5 1.5 7.9 5.6 12 7 7.9 8.4 6.5 12.5 5.1 8.4 1 7 5.1 5.6Z" />
        <path d="M12.8 10.2 13.4 12 15.2 12.6 13.4 13.2 12.8 15 12.2 13.2 10.4 12.6 12.2 12Z" />
      </svg>
      <span aria-hidden="true">AI</span>
      <span id={id} className="ai-assistant-filled-sr-only">{FILLED_DESCRIPTION}</span>
    </span>
  );
}

// Radio choices: the selected option gets the mark; its badge is drawn by CSS on the option's corner.
export function AiAssistantFilledChoice({ path, children }) {
  const field = useAiAssistantFilledField(path);
  const className = ["ai-assistant-filled-choice", field.filled ? "is-filled" : "", field.flashClassName].filter(Boolean).join(" ");
  return (
    <div className={className} onAnimationEnd={field.onAnimationEnd}>
      {children}
      {field.filled ? <span id={field.descriptionId} className="ai-assistant-filled-sr-only">{FILLED_DESCRIPTION}</span> : null}
    </div>
  );
}
